import React, { useRef, useState, useEffect } from 'react';
import {
  Alert, Typography, Box, Button, List, ListItem, ListItemText,
  IconButton, Grid, CircularProgress, Snackbar, Tooltip, Dialog,
  DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoIcon from '@mui/icons-material/Info';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { getMaterialList, extractMaterialsFromApplication } from '../../services/applicationService';
import apiClient from '../../services/api';

// 默认文件组，将被API返回的数据替换
const DEFAULT_FILE_GROUPS = [
  { key: 'idcard', label: '资料包', ifnecessary: 1 },
];

const ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

const API_BASE_URL = process.env.REACT_APP_ATTACHMENT_URL;

const DocumentUpload = ({ onUpload, onNext, onBack, selectedProgram, selectedLevel }) => {
  const [fileGroups, setFileGroups] = useState(DEFAULT_FILE_GROUPS);
  const [filesByGroup, setFilesByGroup] = useState(() => {
    const initial = {};
    DEFAULT_FILE_GROUPS.forEach(g => {
      initial[g.key] = null;
    });
    return initial;
  });



  const fileInputRefs = useRef({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [materialsLoading, setMaterialsLoading] = useState(false);
  
  // 新增：弹窗状态
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogContent, setDialogContent] = useState({ title: '', content: '', type: 'requirement' });

  // 新增：获取材料列表的API请求（每次都调用）
  const fetchMaterialList = async () => {
    if (!selectedProgram || !selectedLevel) {
      return;
    }

    setMaterialsLoading(true);
    try {
      // 构建请求参数
      const s_id = selectedProgram.university?.s_id || selectedProgram.university?.id;
      const params = {
        s_id: s_id,
        degree: selectedLevel === '本科' ? 'Bachelor' : 
                selectedLevel === '硕士' ? 'Master' : 
                selectedLevel === '博士' ? 'Doctor' : 'Bachelor'
      };
      
      const newFileGroups = await getMaterialList(params);
      
      if (newFileGroups) {
        setFileGroups(newFileGroups);
        
        // 处理filesByGroup状态：如果材料有path且不为空，说明已上传
        const newFilesByGroup = {};
        newFileGroups.forEach(g => {
          // 检查材料是否有path且不为空
          if (g.path && g.path.trim() !== '') {
            // 已上传的文件
            newFilesByGroup[g.key] = {
              name: g.name || g.label,
              path: g.path
            };
          } else {
            // 未上传的文件，保留已选择的文件（如果有的话）
            newFilesByGroup[g.key] = filesByGroup[g.key] || null;
          }
        });
        setFilesByGroup(newFilesByGroup);
      } else {
        // 如果API请求失败，使用默认文件组
        setFileGroups(DEFAULT_FILE_GROUPS);
      }
    } catch (error) {
      // 如果API请求失败，使用默认文件组，但不显示错误信息给用户
      setFileGroups(DEFAULT_FILE_GROUPS);
    } finally {
      setMaterialsLoading(false);
    }
  };

  // 新增：每次组件加载或参数变化时都请求材料列表
  useEffect(() => {
    if (selectedProgram && selectedLevel) {
      fetchMaterialList();
    }
  }, [selectedProgram, selectedLevel]);

  // 新增：处理弹窗显示
  const handleShowDialog = (content, type = 'requirement') => {
    setDialogContent({
      title: type === 'requirement' ? '材料要求' : '查看示例',
      content: content,
      type: type
    });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleFileChange = (groupKey, e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!ALLOWED_TYPES.includes(file.type)) {
      setError(`"${file.name}" 文件类型不支持。只允许 PDF 或图片（jpg/png）`);
      e.target.value = '';
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      setError(`"${file.name}" 文件超过 10MB，请上传不超过 10MB 的文件`);
      e.target.value = '';
      return;
    }

    setFilesByGroup(prev => {
      const updated = { ...prev, [groupKey]: file };
      onUpload && onUpload(updated);
      return updated;
    });

    setError(null);
    // 移除这行，不要立即清空input值
    // e.target.value = '';
  };

  const handleDelete = (groupKey) => {
    setFilesByGroup(prev => {
      const updated = { ...prev, [groupKey]: null };
      onUpload && onUpload(updated);
      return updated;
    });
  };

  // 检查必传文件是否都已上传
  const allRequiredUploaded = fileGroups
    .filter(g => g.ifnecessary === 1) // 只检查必传文件
    .every(g => !!filesByGroup[g.key]);

  const handleSubmit = async () => {
    setError(null);
    setLoading(true);
    try {
      const formData = new FormData();

      Object.entries(filesByGroup).forEach(([key, file]) => {
        if (file instanceof File) {
          // 使用材料id作为key上传文件
          formData.append(key, file);
        } else if (file && typeof file === 'object' && file.path) {
          formData.append(`${key}[path]`, file.path);
          formData.append(`${key}[name]`, file.name || 'unknown');
        }
      });

      // 文件上传API调用
      const response = await apiClient.post('/api/application/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (response?.data?.data) {
        // 文件上传成功后，重新获取材料列表以更新状态
        await fetchMaterialList();
      }

      setSuccess(true);
      setLoading(false);

      onNext && onNext();
    } catch (e) {
      console.error('文件上传失败:', e);
      setError(e.message || '上传失败');
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
        第五步：准备材料
      </Typography>
      <Alert severity="info" sx={{ mb: 3 }}>
        请上传以下材料，格式为图片或者 PDF 文件，大小不能超过 10M
      </Alert>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Snackbar
        open={success}
        autoHideDuration={2000}
        onClose={() => setSuccess(false)}
        message="上传成功"
      />
      {materialsLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>正在加载材料列表...</Typography>
        </Box>
      )}
      
      <Grid container spacing={4}>
        {fileGroups.map(group => (
          <Grid item xs={12} md={4} key={group.key}>
            <Box sx={{ 
              border: '1px solid #e0e0e0', 
              borderRadius: 2, 
              p: 2, 
              minHeight: 120,
              position: 'relative',
              backgroundColor: group.ifnecessary === 1 ? '#f8f9fa' : '#ffffff'
            }}>
              <Box sx={{
                position: 'absolute',
                top: -8,
                left: 10,
                backgroundColor: group.ifnecessary === 1 ? '#1976d2' : '#757575',
                color: 'white',
                px: 1,
                py: 0.5,
                borderRadius: 1,
                fontSize: '0.75rem',
                fontWeight: 'bold',
                zIndex: 1
              }}>
                {group.ifnecessary === 1 ? '必选' : '可选'}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mb: 1 }}>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  {group.requirement && (
                    <Tooltip title="查看材料要求">
                      <IconButton 
                        size="small" 
                        onClick={() => handleShowDialog(group.requirement, 'requirement')}
                        sx={{ 
                          color: 'primary.main',
                          '&:hover': {
                            backgroundColor: 'primary.light',
                            color: 'white'
                          }
                        }}
                      >
                        <InfoIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {group.example && (
                    <Tooltip title="查看示例">
                      <IconButton 
                        size="small" 
                        onClick={() => handleShowDialog(group.example, 'example')}
                        sx={{ 
                          color: 'primary.main',
                          '&:hover': {
                            backgroundColor: 'primary.light',
                            color: 'white'
                          }
                        }}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Box>

              {!filesByGroup[group.key] ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 80 }}>
                    <Button
                      variant="contained"
                      component="label"
                      size="medium"
                      sx={{
                        backgroundColor: '#1976d2',
                        '&:hover': {
                          backgroundColor: '#1565c0'
                        }
                      }}
                    >
                      {group.label}
                      <input
                        type="file"
                        accept=".pdf,.jpg,.png,.jpeg"
                        hidden
                        ref={el => (fileInputRefs.current[group.key] = el)}
                        onChange={e => handleFileChange(group.key, e)}
                      />
                    </Button>
                  </Box>
                                ) : (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 80 }}>
                    <List dense sx={{ width: '100%' }}>
                      <ListItem
                        secondaryAction={
                          <IconButton edge="end" onClick={() => handleDelete(group.key)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        }
                      >
                        <ListItemText
                          primary={
                            <Box>
                              {/* 材料名称 */}
                              <Typography 
                                variant="subtitle2" 
                                sx={{ 
                                  fontWeight: 600, 
                                  color: 'text.primary',
                                  mb: 0.5
                                }}
                              >
                                {group.label}
                              </Typography>
                              {/* 文件链接 */}
                              {filesByGroup[group.key] instanceof File ? (
                                <Typography variant="body2" color="text.secondary">
                                  {filesByGroup[group.key].name}
                                </Typography>
                              ) : (
                                <a
                                  href={`${API_BASE_URL}/storage/${filesByGroup[group.key].path?.replace(/\\/g, '/')}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ 
                                    color: '#1976d2', 
                                    textDecoration: 'none',
                                    fontSize: '0.875rem'
                                  }}
                                >
                                  {filesByGroup[group.key].name || '已上传文件'}
                                </a>
                              )}
                            </Box>
                          }
                        />
                      </ListItem>
                    </List>
                  </Box>
                )}
              

            </Box>
          </Grid>
        ))}
      </Grid>
      <Box sx={{ textAlign: 'center', py: 4, display: 'flex', justifyContent: 'center', gap: 2 }}>
        <Button variant="outlined" onClick={onBack} sx={{ minWidth: 120 }} disabled={loading}>
          ← 上一步
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={!allRequiredUploaded || loading}
          sx={{ minWidth: 180, position: 'relative' }}
        >
          {loading && <CircularProgress size={22} sx={{ position: 'absolute', left: 16, top: '50%', mt: '-11px' }} />}
          提交材料，下一步
        </Button>
      </Box>

      {/* 材料要求弹窗 */}
      <Dialog 
        open={dialogOpen && dialogContent.type === 'requirement'} 
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          borderBottom: '1px solid #e0e0e0'
        }}>
          <InfoIcon color="primary" />
          {dialogContent.title}
        </DialogTitle>
        <DialogContent dividers sx={{ py: 2 }}>
          <Typography variant="body1" sx={{ 
            whiteSpace: 'pre-wrap',
            lineHeight: 1.6,
            fontSize: '0.95rem'
          }}>
            {dialogContent.content}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseDialog} color="primary" variant="contained">
            关闭
          </Button>
        </DialogActions>
      </Dialog>

             {/* 查看示例弹窗 */}
       <Dialog 
         open={dialogOpen && dialogContent.type === 'example'} 
         onClose={handleCloseDialog}
         maxWidth="md"
         fullWidth
       >
         <DialogTitle sx={{ 
           display: 'flex', 
           alignItems: 'center', 
           gap: 1,
           borderBottom: '1px solid #e0e0e0'
         }}>
           <VisibilityIcon color="primary" />
           {dialogContent.title}
         </DialogTitle>
         <DialogContent dividers>
           {dialogContent.content && (
             <Box sx={{ textAlign: 'center' }}>
               {dialogContent.content.endsWith('.pdf') ? (
                 <iframe 
                   src={dialogContent.content} 
                   width="100%" 
                   height="500px" 
                   style={{ border: 'none' }}
                   title="示例文档"
                 />
               ) : (
                 <img 
                   src={dialogContent.content} 
                   alt="示例图片" 
                   style={{ maxWidth: '100%', height: 'auto' }}
                 />
               )}
             </Box>
           )}
         </DialogContent>
         <DialogActions>
           <Button onClick={handleCloseDialog} color="primary">
             关闭
           </Button>
           {dialogContent.content && (
             <Button 
               onClick={() => window.open(dialogContent.content, '_blank')} 
               color="primary"
               variant="outlined"
             >
               在新窗口打开
             </Button>
           )}
         </DialogActions>
       </Dialog>
    </Box>
  );
};

export default DocumentUpload;
